/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AfterViewInit, Directive, ElementRef, Inject, OnDestroy} from '@angular/core';

import {formButtonVisibleCheck} from './form-button-visible-check';
import {WINDOW} from '../../../angular-platform-providers';
import {AppEventBusService} from '../../events/app-event-bus.service';

/**
 * Pins Save/Undo (.formButtons) to the bottom of the viewport when the form is
 * long enough that the buttons would otherwise be below the fold.
 * Keeps form action buttons visible while scrolling.
 *
 * Opt out with attribute adnAvoidSticky on the .formButtons element.
 */
@Directive({
  standalone: true,
  selector: '.formButtons:not([adnAvoidSticky])'
})
export class FormButtonsStickyDirective implements AfterViewInit, OnDestroy {
  private cleanupFn: (() => void) | null = null;
  private initTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    @Inject(WINDOW) private readonly window: any,
    private readonly events: AppEventBusService
  ) {
  }

  ngAfterViewInit(): void {
    // SPA navigations often create the next view before the previous one is
    // destroyed. Defer + retry so we only attach once we are the live first
    // .formButtons (a sync check here often sees the outgoing page's node).
    this.queueInit(0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.initTimer !== null) {
      clearTimeout(this.initTimer);
      this.initTimer = null;
    }
    if (this.cleanupFn) {
      this.cleanupFn();
      this.cleanupFn = null;
    }
  }

  private queueInit(attempt: number): void {
    const delays = [0, 0, 50, 100, 200, 500, 1000];
    if (attempt >= delays.length) {
      return;
    }

    this.initTimer = setTimeout(() => {
      this.initTimer = null;
      if (this.destroyed || this.cleanupFn) {
        return;
      }

      const host = this.elementRef.nativeElement;
      if (!host.isConnected) {
        this.queueInit(attempt + 1);
        return;
      }

      const all = this.window.document.getElementsByClassName('formButtons');
      if (!all.length || all[0] !== host) {
        this.queueInit(attempt + 1);
        return;
      }

      this.cleanupFn = formButtonVisibleCheck(
        this.window,
        (listener: (...args: any[]) => void) => this.events.on('SUBMIT_PROCESSED', listener),
        host
      );
    }, delays[attempt]);
  }
}
